'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle, FileText, Package, Plus, Download, Loader2, CloudOff, ExternalLink } from 'lucide-react';
import { generateJobCardPDF } from '@/lib/generateJobCardPDF';
import { JobCardFormData } from './JobCardWizard';

interface JobCardSuccessProps {
  jobNo: string;
  formData: JobCardFormData | null;
  driveLink: string | null;
  drivePath: string | null;
  driveError: string | null;
  onNew: () => void;
}

export default function JobCardSuccess({ jobNo, formData, driveLink, drivePath, driveError, onNew }: JobCardSuccessProps) {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!formData) return;
    setDownloading(true);
    try {
      await generateJobCardPDF(formData);
    } catch (err) {
      console.error('PDF re-download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="card-surface p-10 flex flex-col items-center text-center max-w-xl mx-auto fade-in">
      <div className="w-16 h-16 rounded-full bg-positive/15 flex items-center justify-center mb-4">
        <CheckCircle size={32} className="text-positive" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">Job Card Submitted</h2>
      <p className="text-sm text-muted-foreground mb-1">
        Job card <span className="font-mono-data font-semibold text-primary">{jobNo}</span> has been submitted successfully.
      </p>
      <p className="text-xs text-muted-foreground mb-6">
        PDF generated and downloaded. Inventory deducted. Branch representative notified.
      </p>

      <div className="w-full p-4 rounded-xl border border-positive/20 bg-positive/5 text-left mb-4 space-y-1.5">
        {[
          '✓ Job card saved to database',
          '✓ PDF report generated and downloaded',
          '✓ Inventory stock levels updated',
          '✓ Signature captured and embedded in PDF',
        ].map((item) => (
          <p key={`success-${item}`} className="text-xs text-positive">{item}</p>
        ))}

        {/* Drive upload status */}
        {driveLink ? (
          <p className="text-xs text-positive">✓ PDF saved to Google Drive</p>
        ) : driveError ? (
          <p className="text-xs text-warning">⚠ Google Drive upload failed: {driveError}</p>
        ) : null}
      </div>

      {/* Google Drive path */}
      {driveLink && drivePath && (
        <div className="w-full p-3 rounded-lg border border-primary/20 bg-primary/5 text-left mb-4">
          <p className="text-xs font-semibold text-primary mb-1 flex items-center gap-1.5">
            <FileText size={12} />
            Saved to Google Drive
          </p>
          <p className="font-mono-data text-xs text-muted-foreground mb-2">{drivePath}</p>
          <a
            href={driveLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <ExternalLink size={11} />
            Open in Google Drive
          </a>
        </div>
      )}

      {/* Drive error detail */}
      {driveError && !driveLink && (
        <div className="w-full p-3 rounded-lg border border-warning/20 bg-warning/5 text-left mb-4">
          <p className="text-xs font-semibold text-warning mb-1 flex items-center gap-1.5">
            <CloudOff size={12} />
            Google Drive Upload Failed
          </p>
          <p className="text-xs text-muted-foreground">{driveError}</p>
          <p className="text-xs text-muted-foreground mt-1">
            The PDF was still downloaded locally. Check that <code className="font-mono-data">GOOGLE_SERVICE_ACCOUNT_JSON</code> is set correctly in your environment variables.
          </p>
        </div>
      )}

      {/* PDF filing path fallback */}
      {formData && !driveLink && (
        <div className="w-full p-3 rounded-lg border border-border bg-secondary/20 text-left mb-6">
          <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1.5">
            <FileText size={12} />
            Expected Drive Filing Path
          </p>
          <p className="font-mono-data text-xs text-muted-foreground">
            {formData.branch || '[Branch]'} / {formData.vehicleReg || '[Vehicle Reg]'} / {jobNo}.pdf
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 justify-center">
        {formData && (
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="btn-primary"
          >
            {downloading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Download size={14} />
                Download PDF
              </>
            )}
          </button>
        )}
        <button onClick={onNew} className="btn-secondary">
          <Plus size={14} />
          New Job Card
        </button>
        <Link href="/inventory-management" className="btn-secondary">
          <Package size={14} />
          Check Inventory
        </Link>
        <Link href="/" className="btn-secondary">
          <FileText size={14} />
          Dashboard
        </Link>
      </div>
    </div>
  );
}